import { MapViewerConfiguration } from "../../config/map-viewer-configuration";
import { RootStoreSession } from "../root-store";
import { DataTransporter } from "../data-transporter";
import { computed, makeObservable, observable } from "mobx";
import { Nullable, Optional } from "../../globals/types";
import { CreationProject, UsageProject } from "../project/project";
import _ from "lodash";
import { DataNotFound, DataNotLoaded } from "../../globals/errors";
import { Constants } from "../../globals/constants";
import { ActivityLogEntry } from "../activity-log-entry/activity-log-entry-store";
import moment from "moment";
import { User } from "../user/user-store";

export class CompensationStore {
    mapViewerConfig: MapViewerConfiguration;
    rootStore: RootStoreSession;
    dataTransporter: DataTransporter;
    compensations: Compensation[];

    constructor(mapViewerConfig: MapViewerConfiguration, rootStore: RootStoreSession, dataTransporter: DataTransporter) {
        this.mapViewerConfig = mapViewerConfig;
        this.rootStore = rootStore;
        this.dataTransporter = dataTransporter;
        this.compensations = [];
    }

    async loadAll(): Promise<Compensation[]> {
        this.compensations = [];
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-compensations`);
        const compensationDtos: CompensationDto[] = result.data;
        compensationDtos.forEach(c => this.compensations.push(new Compensation(c, this.rootStore)));
        return this.compensations;
    }

    async loadWithOptions(options: FindFilter): Promise<Compensation[]> {
        this.compensations = [];

        let queryString = "";
        let prefix = "?";

        if (options.creationProjectId !== undefined) {
            queryString += `${prefix}fffCreationProjectId=${options.creationProjectId}`;
            prefix = "&";
        }

        if (options.usageProjectId !== undefined) {
            queryString += `${prefix}fffUsageProjectId=${options.usageProjectId}`;
        }

        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-compensations${queryString}`);
        const compensationDtos: CompensationDto[] = result.data;
        compensationDtos.forEach(c => this.compensations.push(new Compensation(c, this.rootStore)));
        return this.compensations;
    }

    async load(id: number): Promise<Compensation> {
        const result = await this.dataTransporter.get(`${this.mapViewerConfig.api_base_url}/fff-compensations/${id}`);
        const compensationDto: CompensationDto = result.data;
        const compensation = new Compensation(compensationDto, this.rootStore);
        this.compensations.push(compensation);
        return compensation;
    }

    async add(compensationDto: CompensationDto): Promise<Compensation> {
        const result = await this.dataTransporter.post(`${this.mapViewerConfig.api_base_url}/fff-compensations`, compensationDto);
        const compensationDtoResult: CompensationDto = result.data;
        const compensation = new Compensation(compensationDtoResult, this.rootStore);
        this.compensations.push(compensation);
        return compensation;
    }

    async update(compensationDto: CompensationDto): Promise<Compensation> {
        const compensation = this.compensations.find(c => c.id == compensationDto.id);

        if (compensation !== undefined) {
            const result = await this.dataTransporter.put(`${this.mapViewerConfig.api_base_url}/fff-compensations/${compensationDto.id}`, compensationDto);
            compensation.compensationDto = result.data;
            return compensation;
        }

        throw new DataNotFound("Compensation not found");
    }

    async delete(id: number): Promise<Compensation> {
        await this.dataTransporter.delete(`${this.mapViewerConfig.api_base_url}/fff-compensations/${id}`);
        const removedCompensations: Compensation[] = _.remove(this.compensations, c => c.id === id);
        return removedCompensations[0];
    }
}

export class Compensation {
    private updatedBy_?: User;
    private updatedAtTimestamp_?: number;
    compensationDto: CompensationDto;
    rootStore: RootStoreSession;

    constructor(compensationDto: CompensationDto, rootStore: RootStoreSession) {
        this.compensationDto = compensationDto;
        this.rootStore = rootStore;
        makeObservable(this, {
            compensationDto: observable,
            id: computed,
            usageProjectId: computed,
            usageProject: computed,
            creationProjectId: computed,
            creationProject: computed,
            areaNek2: computed,
            areaNek3: computed,
            areaNek4: computed,
            areaNek5: computed,
            areaNek6: computed,
            areaNekUnknown: computed,
            area: computed,
            remarks: computed,
            updatedAtTimestamp: computed,
            updatedAt: computed,
            updatedBy: computed,
            completed: computed
        })
    }

    get id(): number {
        return this.compensationDto.id;
    }

    get usageProjectId(): number {
        return this.compensationDto.fffUsageProjectId;
    }

    get usageProject(): UsageProject {
        const project = this.rootStore.projectStore.usageProjects.find(p => p.id === this.usageProjectId);

        if (project !== undefined) {
            return project;
        }

        throw new DataNotLoaded("Usage Project not loaded");
    }

    get creationProjectId(): number {
        return this.compensationDto.fffCreationProjectId;
    }

    get creationProject(): CreationProject {
        const project = this.rootStore.projectStore.creationProjects.find(p => p.id === this.creationProjectId);

        if (project !== undefined) {
            return project;
        }

        throw new DataNotLoaded("Creation Project not loaded");
    }

    get areaNek2(): Optional<number> {
        return this.compensationDto.areaNek2 ?? undefined;
    }

    get areaNek3(): Optional<number> {
        return this.compensationDto.areaNek3 ?? undefined;
    }

    get areaNek4(): Optional<number> {
        return this.compensationDto.areaNek4 ?? undefined;
    }

    get areaNek5(): Optional<number> {
        return this.compensationDto.areaNek5 ?? undefined;
    }

    get areaNek6(): Optional<number> {
        return this.compensationDto.areaNek6 ?? undefined;
    }

    get areaNekUnknown(): Optional<number> {
        return this.compensationDto.areaNekUnknown ?? undefined;
    }

    get area(): Optional<number> {
        return this.compensationDto.area ?? undefined;
    }

    get remarks(): Optional<string> {
        return this.compensationDto.remarks ?? undefined;
    }

    get updatedAtTimestamp(): number {
        if (this.updatedAtTimestamp_ === undefined) {
            const updateEntries = this.rootStore.activityLogEntryStore.activityLogEntries[Constants.compensationTableName]
                .filter(e => e.rowId === this.id && e.action === "update");
            const sortedUpdateEntries: ActivityLogEntry[] = _.sortBy(updateEntries, ["timestamp"]);
            const timestamp = _.last<ActivityLogEntry>(sortedUpdateEntries)?.timestamp;

            if (timestamp !== undefined) {
                this.updatedAtTimestamp_ = timestamp;
            } else {
                throw new Error();
            }
        }

        return this.updatedAtTimestamp_!;
    }

    get updatedAt(): string {
        return moment.unix(this.updatedAtTimestamp).format(Constants.timestampFormat);
    }

    get updatedBy(): User {
        if (this.updatedBy_ === undefined) {
            const updateEntries = this.rootStore.activityLogEntryStore.activityLogEntries[Constants.compensationTableName]
                .filter(e => e.rowId === this.id && e.action === "update");
            const sortedUpdateEntries: ActivityLogEntry[] = _.sortBy(updateEntries, ["timestamp"]);
            const userId = _.last<ActivityLogEntry>(sortedUpdateEntries)?.userId;
            const user = this.rootStore.userStore.users.find(u => u.id === userId);

            if (user !== undefined) {
                this.updatedBy_ = user;
            } else {
                throw new DataNotLoaded("User not loaded");
            }
        }

        return this.updatedBy_;
    }

    get completed(): boolean {
        return this.creationProject.areas.every(a =>
            a.areaDto.creationStatus === "abgeschlossen" ||
            a.areaDto.creationStatus === "abgeschlossen_mit_maengel"
        );
    }
}

export class CompensationDtoFactory {
    static makeCompensationDto(formData: FormData): CompensationDto {
        const formDataObject: any = Object.fromEntries(
            Array.from(formData).filter(([_, v]) => v != null && v !== "")
        );

        const area = parseFloat(formDataObject.area);
        delete formDataObject.maxArea;
        const compensationDto: CompensationDto = formDataObject;
        compensationDto.area = area;
        return compensationDto;
    }
}

export type CompensationDto = {
    id: number;
    fffUsageProjectId: number;
    fffCreationProjectId: number;
    areaNek2: Nullable<number>;
    areaNek3: Nullable<number>;
    areaNek4: Nullable<number>;
    areaNek5: Nullable<number>;
    areaNek6: Nullable<number>;
    areaNekUnknown: Nullable<number>;
    area: Nullable<number>;
    remarks: Nullable<string>;
};

type FindFilter = {
    creationProjectId?: number;
    usageProjectId?: number;
};